import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackPageView } from '../utils/analytics';

type Route = 'home' | 'about' | 'plate-detail' | 'privacy' | 'terms' | 'faq';

interface RouterContextType {
  currentRoute: Route;
  plateId: string | null;
  navigate: (route: Route, plateId?: string) => void;
}

const RouterContext = createContext<RouterContextType | undefined>(undefined);

export const useRouter = () => {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error('useRouter must be used within a RouterProvider');
  }
  return context;
};

interface RouterProviderProps {
  children: React.ReactNode;
}

export const RouterProvider: React.FC<RouterProviderProps> = ({ children }) => {
  const [currentRoute, setCurrentRoute] = useState<Route>('home');
  const [plateId, setPlateId] = useState<string | null>(null);

  const getPathFromRoute = (route: Route, plateIdParam?: string) => {
    if (route === 'about') return '/about';
    if (route === 'privacy') return '/privacy';
    if (route === 'terms') return '/terms';
    if (route === 'faq') return '/faq';
    if (route === 'plate-detail' && plateIdParam) return `/plate/${plateIdParam}`;
    return '/';
  };

  const getPageTitle = (path: string) => {
    if (path === '/about') return 'About - Rate My Plate | Custom License Plate Community';
    if (path === '/privacy') return 'Privacy Policy - Rate My Plate';
    if (path === '/terms') return 'Terms of Service - Rate My Plate';
    if (path === '/faq') return 'FAQ - License Plate Ideas &amp; How to Share | Rate My Plate';
    if (path.startsWith('/plate/')) return 'License Plate Detail - Rate My Plate';
    return 'Rate My Plate - Funny &amp; Custom License Plate Ideas | R8MYPL8';
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/about') {
        setCurrentRoute('about');
        setPlateId(null);
      } else if (path === '/privacy') {
        setCurrentRoute('privacy');
        setPlateId(null);
      } else if (path === '/terms') {
        setCurrentRoute('terms');
        setPlateId(null);
      } else if (path === '/faq') {
        setCurrentRoute('faq');
        setPlateId(null);
      } else if (path.startsWith('/plate/')) {
        const id = path.split('/plate/')[1];
        setCurrentRoute('plate-detail');
        setPlateId(id);
      } else {
        setCurrentRoute('home');
        setPlateId(null);
      }
      const title = getPageTitle(path);
      document.title = title.replace(/&amp;/g, '&');
      trackPageView(path, title);
    };

    window.addEventListener('popstate', handlePopState);
    
    // Set initial route based on URL
    handlePopState();

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (route: Route, plateIdParam?: string) => {
    setCurrentRoute(route);
    setPlateId(plateIdParam || null);
    
    const path = getPathFromRoute(route, plateIdParam);
    window.history.pushState({}, '', path);
    
    const title = getPageTitle(path);
    document.title = title.replace(/&amp;/g, '&');
    trackPageView(path, title);
  };

  return (
    <RouterContext.Provider value={{ currentRoute, plateId, navigate }}>
      {children}
    </RouterContext.Provider>
  );
};
